import {html, LitElement, nothing} from "lit";
import {customElement, property, query, state} from "lit/decorators.js";
import {classMap} from "lit/directives/class-map.js";
import {styleMap} from "lit/directives/style-map.js";

import "../common/controls-inputs/IconButton.js";
import "./annotations/AnnotationBookmark.js";
import "./annotations/AnnotationFreeform.js";
import "./annotations/AnnotationHighlight.js";
import "./annotations/AnnotationImage.js";
import {MobxLitElement} from "@adobe/lit-mobx";
import UI_MODES from "./ui_modes.js";

import {outlineSectionStyles} from "./OutlineSection.styles.js";
import Data from "../../main/data/index.js";
import Project from "../../main/project.js";
import {annotation, topic} from "../../main/data/model/index.js";

@customElement("pn-outline-section")
export class OutlineSection extends MobxLitElement {
	// Allow skip link to work by focusing an outline section, which will then
	// focus the first focusable element inside it
	static shadowRootOptions = {...LitElement.shadowRootOptions, delegatesFocus: true};

	static styles = [outlineSectionStyles];

	@property() annotations = [];
	@property() color = "000000";
	@property({type: Boolean}) isEditable = false;
	@property({type: Boolean}) hasTopicsEditingAccess = false;
	@property() label = "Label";
	@property({type: String}) renderString = "";
	@property() section:              any;
	@property() selectedView:         string;
	@property({type: String}) sortBy = "topic";
	@property({type: UI_MODES}) mode: UI_MODES;

	@state() isAddingNewFreeform = false;
	@state() isEditingTopicName = false;
	@state() isOpen = true;

	@query("input.js-aa-label") _topicNameInput: HTMLInputElement;

	async firstUpdated() {
		if (Data.app.hasStorage) {
			const storageKey = this.collapsedSectionsKey;
			const sectionKey = this.sectionKey;

			// This next line being async causes the component to render and then, if isOpen changes,
			// render again, causing the little arrow animation. We should probably fix that.
			const collapsed: string[] = await Data.app.storage?.getOne(storageKey) ?? [];
			const isCollapsed = collapsed.includes(sectionKey);
			if (this.isOpen && isCollapsed)
				this.isOpen = false;
			else if (!this.isOpen && !isCollapsed)
				this.isOpen = true;
		}
	}

	onClickSectionToggle = async () => {
		this.isOpen = !this.isOpen;

		if (Data.app.hasStorage) {
			const storageKey = this.collapsedSectionsKey;
			const sectionKey = this.sectionKey;

			const collapsed: string[] = await Data.app.storage?.getOne(storageKey) ?? [];

			if (this.isOpen && collapsed.includes(sectionKey))
				collapsed.splice(collapsed.indexOf(sectionKey), 1);
			else if (!this.isOpen && !collapsed.includes(sectionKey))
				collapsed.push(sectionKey);

			await Data.app.storage?.setOne(storageKey, collapsed);
		}
	};

	private get collapsedSectionsKey(): string {
		return "outline-collapsed-sections:" + Data.app.project?.uuid;
	}

	private get sectionKey(): string {
		return this.sortBy + ":" + this.section.label;
	}

	onClickEditTopicName = async (evt: PointerEvent) => {
		evt.preventDefault();
		evt.stopPropagation();

		this.isEditingTopicName = true;

		// Wait for the <input /> to be rendered
		await this.updateComplete;

		this._topicNameInput.focus();
		this._topicNameInput.select();
	};

	handleBlurOnTopicEditName = async (evt: FocusEvent) => {
		const topicUuid = this.section.label;
		const newValue = (evt.target as HTMLInputElement)?.value;

		await Data.topic.update(topicUuid, {label: newValue});

		this.isEditingTopicName = false;
	};

	handleKeyDownOnTopicEditName = (event: KeyboardEvent) => {
		const target = event.target as HTMLInputElement;
		const key = event.key;

		if (key === "Enter" || key === "Escape" || key === "Esc")
			target.blur();
	};

	handleClickOnAddFreeformNote = async () => {
		if (!this.isOpen)
			this.isOpen = true;

		this.isAddingNewFreeform = true;
	};

	renderNewFreeform() {
		if (!this.isAddingNewFreeform)
			return nothing;

		const onDeleteAnnotation = () => {
			this.isAddingNewFreeform = false;
		};

		return html`
			<pn-annotation-freeform
				.annotation=${{
					comment: "",
					uuid:    "pn-annotation-editor-new-freeform"
				}}
				id="new-freeform-note"
				.isNew=${true}
				.onDeleteAnnotation=${onDeleteAnnotation}
				.topic=${this.section.topic}
				.sortBy=${this.sortBy}
				.mode=${this.mode}
			></pn-annotation-freeform>
		`;
	}

	renderAnnotations(annotations: {annotation: annotation; topic: topic; createdByName: string}[]) {
		return annotations.map(a => {
			const {annotation, topic, createdByName} = a;

			switch (annotation.type) {
				case "bookmark":
					return html`
						<pn-annotation-bookmark
							.annotation=${annotation}
							.createdByName=${createdByName}
							.isEditable=${this.isEditable}
							.topic=${topic}
							.sortBy=${this.sortBy}
							.mode=${this.mode}
						></pn-annotation-bookmark>
					`;

				case "freeform":
					if (this.selectedView === "bookmarks")
						return nothing;

					return html`
						<pn-annotation-freeform
							.annotation=${annotation}
							.createdByName=${createdByName}
							.isEditable=${this.isEditable}
							.topic=${topic}
							.sortBy=${this.sortBy}
							.mode=${this.mode}
						></pn-annotation-freeform>
					`;

				case "highlight":
					if (this.selectedView === "bookmarks")
						return nothing;

					return html`
						<pn-annotation-highlight
							.annotation=${annotation}
							.createdByName=${createdByName}
							.isEditable=${this.isEditable}
							.topic=${topic}
							.sortBy=${this.sortBy}
							.mode=${this.mode}
						></pn-annotation-highlight>
					`;

				case "image":
					if (this.selectedView === "bookmarks")
						return nothing;

					return html`
						<pn-annotation-image
							.annotation=${annotation}
							.createdByName=${createdByName}
							.isEditable=${this.isEditable}
							.topic=${topic}
							.sortBy=${this.sortBy}
							.mode=${this.mode}
						></pn-annotation-image>
					`;

				default:
					throw new Error("Unknown annotation type for uuid: " + annotation.uuid);
			}
		});
	}

	renderSectionLabel(sectionColor: string) {
		const {render: renderString} = this.section;

		const classes = "js-aa-label section-label-text text-ellipsis";

		if (this.isEditingTopicName) {
			return html`
				<input
					@blur=${this.handleBlurOnTopicEditName}
					@click=${(evt: PointerEvent) => evt.stopPropagation()}
					class=${classes}
					@keydown=${this.handleKeyDownOnTopicEditName}
					style=${styleMap({color: sectionColor})}
					type="text"
					value=${renderString}
				/>
			`;
		} else {
			return html`
				<span
					class=${classes}
					style=${styleMap({color: sectionColor})}
					lang=""
				>
					${renderString}
				</span>
			`;
		}

	}

	render() {
		switch (this.mode) {
			case UI_MODES.SIDEBAR:
			case UI_MODES.SIDEBAR_DRAG:
			case UI_MODES.SIDEBAR_READONLY:
				break;
			default:
				return "Unsupported outline mode: " + this.mode;
		}

		const {annotations, topic, label} = this.section;
		if (this.mode === UI_MODES.SIDEBAR_DRAG && annotations.length === 0)
			return null;

		const sectionColor = (this.sortBy === "topic")
			? "#" + (topic?.color ?? "000000")
			: "#000000";

		const showAddFreeformButton
			= (this.mode !== UI_MODES.SIDEBAR_READONLY)
			&& (this.mode !== UI_MODES.SIDEBAR_DRAG)
			&& topic && topic.type !== "bookmarks";
		const showEditButton = showAddFreeformButton && Project.hasTopicsEditingAccess(Data.app.project);

		const typeString = topic ? "topic" : "section";

		return html`
			<div
				class="${classMap({
					"outline-section":       true,
					"js-aa-outline-section": true,
					open:                    this.isOpen,
					"mode-sidebar-drag":     this.mode === UI_MODES.SIDEBAR_DRAG
					// "ui-state-standard":     this.mode === UI_MODES.STANDARD
				})}"
				data-section-label=${label}
			>
				<div class="section-header">
					<button
						aria-label=${`Toggle ${typeString}`}
						class="section-btn-toggle js-aa-btn-section-toggle dotted-focus-outline"
						@click=${this.onClickSectionToggle}
					>
						<i class="icon-chevron-light-down" style=${styleMap({display: "block"})}></i>
					</button>

					<div
						class=${classMap({
							"section-label":            true,
							"js-aa-btn-section-toggle": true,
							"editing":                  this.isEditingTopicName
						})}
						@click=${this.onClickSectionToggle}
						style=${styleMap({color: sectionColor})}
						tabindex="-1"
					>
						${this.renderSectionLabel(sectionColor)}
						<span
							class="js-aa-ct count-indicator"
							style=${styleMap({background: sectionColor})}
						>${annotations.length}</span>
						${showEditButton ? html`<pn-icon-button
							class="js-aa-btn-edit-topic-name edit-topic-name"
							icon="edit"
							.onClick=${this.onClickEditTopicName}
							tippyOptions=${{placement: "bottom"}}
							tooltipText="Edit Topic Name"
						></pn-icon-button>` : null}
					</div>
					${showAddFreeformButton ? html`<pn-icon-button
						class="topic-btn-addnote js-aa-btn-topic-addnote"
						icon="freeform-note"
						.onClick=${this.handleClickOnAddFreeformNote}
						.tippyOptions=${{placement: "bottom"}}
						tooltipText="Add Freeform Note"
					></pn-icon-button>` : null}
				</div>

				<div role="list" class="js-aa-list list">
					${this.renderNewFreeform()}
					${this.renderAnnotations(annotations)}
				</div>
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"pn-outline-section": OutlineSection;
	}
}
